import {
  RECORD_EXTENSION,
  createRecord,
  makeRecordFilename,
  updateRecord,
  validateRecord,
} from '../lib/record.js';

export class JsonLibrary {
  constructor(handleStore) {
    this.handleStore = handleStore;
    this.directoryHandle = null;
  }

  get connected() {
    return Boolean(this.directoryHandle);
  }

  async chooseDirectory() {
    if (!globalThis.showDirectoryPicker) {
      throw new Error('当前浏览器不支持目录访问，请使用最新版 Chrome 或 Edge。');
    }
    const handle = await globalThis.showDirectoryPicker({ mode: 'readwrite' });
    this.directoryHandle = handle;
    await this.handleStore.setDirectory(handle);
    return handle;
  }

  async findStoredDirectory() {
    this.directoryHandle = await this.handleStore.getDirectory();
    return this.directoryHandle;
  }

  async permissionState() {
    if (!this.directoryHandle) return 'missing';
    if (!this.directoryHandle.queryPermission) return 'prompt';
    return this.directoryHandle.queryPermission({ mode: 'readwrite' });
  }

  async requestPermission() {
    if (!this.directoryHandle) return false;
    if (!this.directoryHandle.requestPermission) return false;
    const state = await this.directoryHandle.requestPermission({ mode: 'readwrite' });
    return state === 'granted';
  }

  async scan() {
    this.#assertConnected();
    const jobs = [];
    for await (const entry of this.directoryHandle.values()) {
      if (entry.kind === 'file' && entry.name.endsWith(RECORD_EXTENSION)) {
        jobs.push(this.#readEntry(entry));
      }
    }
    const results = await Promise.all(jobs);
    const records = [];
    const warnings = [];
    for (const result of results) {
      if (result.ok) records.push(result.value);
      else warnings.push(result.warning);
    }
    records.sort((a, b) => b.record.updatedAt.localeCompare(a.record.updatedAt));
    return { records, warnings };
  }

  async save({ current, title, note, data }) {
    this.#assertConnected();
    let record;
    let fileName;

    if (current) {
      record = updateRecord(current.record, { title, note, data });
      fileName = current.fileName;
    } else {
      record = createRecord({ title, note, data });
      fileName = makeRecordFilename(record.title, record.id);
    }

    const fileHandle = await this.directoryHandle.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    try {
      await writable.write(`${JSON.stringify(record, null, 2)}\n`);
      await writable.close();
    } catch (error) {
      await writable.abort?.();
      throw error;
    }
    return { record, fileName, fileHandle };
  }

  async #readEntry(fileHandle) {
    try {
      const file = await fileHandle.getFile();
      const raw = await file.text();
      const validation = validateRecord(JSON.parse(raw));
      if (!validation.ok) throw new Error(validation.error);
      return {
        ok: true,
        value: { record: validation.value, fileName: fileHandle.name, fileHandle },
      };
    } catch (error) {
      return {
        ok: false,
        warning: { fileName: fileHandle.name, message: error.message || String(error) },
      };
    }
  }

  #assertConnected() {
    if (!this.directoryHandle) throw new Error('请先选择资料库目录。');
  }
}
